import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiService from '../../../../services/api';
import { jsPDF } from 'jspdf';
import logo from '../../../../assets/VisibeenLogo.png';

// axios not required; ApiService is our unified HTTP layer
import {
  Box,
  Grid,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Paper,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import {
  PersonAdd as PersonAddIcon,
  Badge as BadgeIcon,
  Business as BusinessIcon,
  School as SchoolIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarTodayIcon,
  Work as WorkIcon,
  Home as HomeIcon,
  Comment as CommentIcon,
  CloudUpload as CloudUploadIcon,
  VpnKey as VpnKeyIcon,
  ContentCopy as ContentCopyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';

import logger from '../../../../utils/logger';
// Premium container with background
const Container = styled(Box)(({ theme }) => ({
  padding: '32px',
  backgroundColor: '#f5f7fa',
  minHeight: '100vh',
}));

// Header Section matching EmployeeListTable
const TitleSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '32px',
}));

const TitleIcon = styled(Box)(({ theme }) => ({
  width: '48px',
  height: '48px',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
}));

const FormPaper = styled(Paper)(({ theme }) => ({
  maxWidth: '960px',
  margin: '0 auto',
  padding: '32px',
  borderRadius: '20px',
  boxShadow: '0 4px 25px rgba(0,0,0,0.05)',
  border: '1px solid #eef0f3',
  backgroundColor: '#fff',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '18px',
  fontWeight: 700,
  color: '#3b82f6',
  marginBottom: '24px',
  marginTop: '32px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  '&:first-of-type': {
    marginTop: 0,
  },
  '&::after': {
    content: '""',
    flex: 1,
    height: '1px',
    background: 'linear-gradient(to right, #dbeafe, transparent)',
  }
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  padding: '12px 40px',
  fontWeight: 700,
  textTransform: 'none',
  fontSize: '16px',
  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  borderRadius: '12px',
  boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
  '&:hover': {
    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
    boxShadow: '0 6px 20px rgba(59, 130, 246, 0.5)',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#fcfcfd',
    '&:hover fieldset': {
      borderColor: '#3b82f6',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#3b82f6',
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#3b82f6',
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#fcfcfd',
    '&:hover fieldset': {
      borderColor: '#3b82f6',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#3b82f6',
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#3b82f6',
  },
}));

const AddEmployee = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    full_name: '',
    employee_code: '',
    department: '',
    education: '',
    email: '',
    phone_number: '',
    alternate_phone_number: '',
    date_of_joining: '',
    employee_type: '',
    designation: '',
    family_member_name: '',
    family_relation: '',
    family_contact: '',
    current_address_line1: '',
    current_city: '',
    current_state: '',
    current_zip_code: '',
    current_country: 'India',
    permanent_address_line1: '',
    permanent_city: '',
    permanent_state: '',
    permanent_zip_code: '',
    permanent_country: 'India',
    report_to: '',
    custom_department: '',
    custom_family_relation: ''
  });

  const [managers, setManagers] = useState([]);
  const [managersLoading, setManagersLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [errors, setErrors] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedDocumentPath, setUploadedDocumentPath] = useState('');
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const fileInputRef = useRef(null);

  // -- Multi-Docs State & Handlers --
  const [documents, setDocuments] = useState([{ title: '', file: null, path: '' }]);

  const handleAddDocument = () => {
    setDocuments([...documents, { title: '', file: null, path: '' }]);
  };

  const handleRemoveDocument = (index) => {
    const list = [...documents];
    list.splice(index, 1);
    setDocuments(list);
  };

  const handleDocTitleChange = (index, val) => {
    const list = [...documents];
    list[index].title = val;
    setDocuments(list);
  };

  const handleDocFileChange = async (index, e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('document', file);
      setSnackbar({ open: true, message: 'Uploading...', severity: 'info' });

      const response = await ApiService.post('/admin/employee/upload-document', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const path = response?.data?.document_path || response?.document_path;
      if (path) {
        const list = [...documents];
        list[index].file = file;
        list[index].path = path;
        setDocuments(list);
        setSnackbar({ open: true, message: 'Document uploaded successfully!', severity: 'success' });
      }
    } catch (error) {
      logger.error('Error uploading document:', error);
      setSnackbar({ open: true, message: 'Failed to upload document.', severity: 'error' });
    }
  };
  // ---------------------------------

  const departments = ['Accounts', 'BackOffice', 'Sales', 'Tele Team', 'Development', 'Other'];
  const employeeTypes = ['Full Time', 'Part Time', 'Contract', 'Intern', 'Consultant'];
  const educations = ['10th', '12th', 'Diploma', 'Graduate', 'Post Graduate', 'Doctorate'];
  const relations = ['Father', 'Mother', 'Brother', 'Sister', 'Other'];
  const roles = [
    { id: 1, name: 'Super Admin', loginRole: 1 },
    { id: 2, name: 'Admin', loginRole: 2 },
    { id: 'bdm_outside', name: 'BDM Sales (outside)', loginRole: 4 },
    { id: 'bdm_inside', name: 'BDM Sales (inside)', loginRole: 4 },
    { id: 'cro', name: 'CRO', loginRole: 2 },
  ];

  // State for role and credentials
  const [selectedRole, setSelectedRole] = useState('');
  const [generatedCredentials, setGeneratedCredentials] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);

  // Fetch managers from API on mount
  useEffect(() => {
    const fetchManagers = async () => {
      try {
        setManagersLoading(true);
        const response = await ApiService.get('/admin/employee/get-managers');
        if (response && response.data) {
          setManagers(response.data);
        } else if (Array.isArray(response)) {
          setManagers(response);
        }
      } catch (error) {
        logger.error('Error fetching managers:', error);
        // Fallback to empty array if fetch fails
        setManagers([]);
      } finally {
        setManagersLoading(false);
      }
    };
    fetchManagers();
  }, []);

  const getToken = () => localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('access_token');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.full_name) newErrors.full_name = 'Full Name is required';
    if (!formData.employee_code) newErrors.employee_code = 'Employee Code is required';
    if (!formData.department) newErrors.department = 'Department is required';
    if (!formData.education) newErrors.education = 'Education is required';
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.phone_number) newErrors.phone_number = 'Contact Number is required';
    if (!formData.date_of_joining) newErrors.date_of_joining = 'Date of Joining is required';
    if (!formData.employee_type) newErrors.employee_type = 'Employee Type is required';
    if (!formData.current_address_line1) newErrors.current_address_line1 = 'Residential Address is required';
    if (!formData.current_state) newErrors.current_state = 'State is required';
    if (!formData.permanent_address_line1) newErrors.permanent_address_line1 = 'Permanent Address is required';
    if (!formData.permanent_state) newErrors.permanent_state = 'State is required';
    if (managers.length > 0 && !formData.report_to) newErrors.report_to = 'Reporting To is required';
    if (!selectedRole) newErrors.role = 'Role is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Generate password function: name@6digitrandom#
  const generatePassword = () => {
    const firstName = formData.full_name.trim().split(' ')[0] || 'user';
    const random6Digits = Math.floor(100000 + Math.random() * 900000);
    const password = `${firstName}@${random6Digits}#`.replace(/\s/g, '');
    const username = formData.email || formData.official_email || '';

    setGeneratedCredentials({
      username: username,
      password: password
    });
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: 'Copied to clipboard!', severity: 'success' });
  };

  // Download Credentials PDF
  const downloadCredentialsPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // Add Logo
    // Assuming logo is imported as base64 or URL. If it's a path, might need handling.
    // For now, let's use a placeholder or assume it loads if it's imported correctly.
    try {
      doc.addImage(logo, 'PNG', pageWidth / 2 - 25, 15, 50, 15); // Adjust dimensions as needed
    } catch (err) {
      console.warn('Logo could not be added', err);
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.setTextColor(59, 130, 246); // #3b82f6
    doc.text("Employee Credentials", pageWidth / 2, 45, { align: "center" });

    doc.setDrawColor(59, 130, 246);
    doc.setLineWidth(0.5);
    doc.line(20, 50, pageWidth - 20, 50);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.setTextColor(60, 60, 60);
    doc.text(`Employee Name: ${formData.full_name}`, 20, 70);
    doc.text(`Designation: ${formData.designation || 'N/A'}`, 20, 80);
    doc.text(`Department: ${formData.department}`, 20, 90);
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, 100);

    // Credentials Box
    doc.setFillColor(252, 252, 253); // Light gray background
    doc.setDrawColor(220, 220, 220);
    doc.rect(20, 115, pageWidth - 40, 60, 'FD');

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(31, 41, 55);
    doc.text("Login Details", 30, 130);

    doc.setFontSize(11);
    doc.setTextColor(107, 114, 128); // Gray text
    doc.text("USERNAME (Official Email)", 30, 145);

    doc.setFont("courier", "bold");
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(generatedCredentials.username, 30, 153);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(107, 114, 128);
    doc.text("PASSWORD", 30, 168);

    doc.setFont("courier", "bold");
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(generatedCredentials.password, 30, 176);

    // Footer
    doc.setFont("helvetica", "italic");
    doc.setFontSize(10);
    doc.setTextColor(150, 150, 150);
    doc.text("Please keep these coordinates secure. Change password after first login.", pageWidth / 2, 195, { align: "center" });

    doc.save(`${formData.full_name.replace(/\s+/g, '_')}_Credentials.pdf`);
  };

  const handleCloseSuccessDialog = () => {
    setShowSuccessDialog(false);
    navigate('/admin-dashboard/employee');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setSnackbar({ open: true, message: 'Please fill all required fields.', severity: 'error' });
      return;
    }

    setLoading(true);
    try {
      const nameParts = formData.full_name.trim().split(' ');
      const first_name = nameParts[0] || '';
      const last_name = nameParts.slice(1).join(' ') || '';

      // Auto-generate password
      const random6Digits = Math.floor(100000 + Math.random() * 900000);
      const autoPassword = `${first_name}@${random6Digits}#`.replace(/\s/g, '');

      // Update state so it can be shown/downloaded
      setGeneratedCredentials({
        username: formData.email || formData.official_email || '',
        password: autoPassword
      });

      // Find the selected role object to get the correct loginRole
      const selectedRoleObj = roles.find(r => String(r.id) === String(selectedRole));
      const loginRoleId = selectedRoleObj?.loginRole || 4; // Default to Employee (4)
      const roleLabel = selectedRoleObj?.name || 'BDM';

      // Auto-set designation for BDM Sales, CRO if not already set
      const finalDesignation = formData.designation ||
        (selectedRole === 'bdm_outside' ? 'BDM Sales (outside)' :
          selectedRole === 'bdm_inside' ? 'BDM Sales (inside)' :
            selectedRole === 'cro' ? 'CRO' : '');

      // Auto-set department for certain roles
      const finalDepartment = formData.department === 'Other'
        ? formData.custom_department
        : (formData.department ||
          ((selectedRole === 'bdm_outside' || selectedRole === 'bdm_inside') ? 'Sales' : formData.department));

      const payload = {
        first_name: first_name,
        last_name: last_name,
        role_id: loginRoleId, // Use the actual login role (BDM/Telly/Sales all map to 4)
        role_label: roleLabel, // Store the descriptive role name
        password: autoPassword, // Send the generated password
        report_to: formData.report_to !== '' && formData.report_to !== null ? Number(formData.report_to) : undefined,
        email: formData.email,
        employee_code: formData.employee_code,
        department: finalDepartment,
        official_email: formData.official_email || formData.email || '',
        phone_number: formData.phone_number,
        alternate_phone_number: formData.alternate_phone_number,
        date_of_joining: formData.date_of_joining,
        employee_type: formData.employee_type,
        designation: finalDesignation,
        education: formData.education,
        education: formData.education,
        document_path: documents[0]?.path || '',
        comment: JSON.stringify(documents.filter(d => d.path && d.title).map(d => ({ title: d.title, url: d.path }))),

        family_member_name: formData.family_member_name,
        family_relation: formData.family_relation === 'Other' ? formData.custom_family_relation : formData.family_relation,
        family_contact: formData.family_contact,

        // Permanent Address
        permanent_address_line1: formData.permanent_address_line1,
        permanent_city: formData.permanent_city,
        permanent_state: formData.permanent_state,
        permanent_zip_code: formData.permanent_zip_code,
        permanent_country: formData.permanent_country,

        // Current Address
        current_address_line1: formData.current_address_line1,
        current_city: formData.current_city,
        current_state: formData.current_state,
        current_zip_code: formData.current_zip_code,
        current_country: formData.current_country,
      };

      // Post like AddLead does (boolean-y success)
      const response = await ApiService.post('/admin/employee/add-employee', payload);

      if (response) {
        setSnackbar({ open: true, message: 'Employee added successfully!', severity: 'success' });
        // Show Success Dialog instead of navigating immediately
        setShowSuccessDialog(true);

        // Clear form (optional, but good practice since we are staying on page)
        setFormData({
          full_name: '',
          employee_code: '',
          department: '',
          education: '',
          email: '',
          phone_number: '',
          alternate_phone_number: '',
          date_of_joining: '',
          employee_type: '',
          family_member_name: '',
          family_relation: '',
          family_contact: '',
          current_address_line1: '',
          current_city: '',
          current_state: '',
          current_zip_code: '',
          current_country: 'India',
          permanent_address_line1: '',
          permanent_city: '',
          permanent_state: '',
          permanent_zip_code: '',
          permanent_country: 'India',
          report_to: '',
          designation: '',
          custom_department: ''
        });
        setSelectedFile(null);
        setUploadedDocumentPath('');
        setSelectedRole('');
      } else {
        throw new Error('Failed to add employee');
      }
    } catch (error) {
      const raw = error?.response?.data || error?.body || error?.message;
      const backendMsg = typeof raw === 'string' ? raw : (raw?.message || raw?.error || JSON.stringify(raw));
      logger.error('Error adding employee:', error);
      setSnackbar({ open: true, message: backendMsg || 'Failed to add employee. Please try again.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));
  const handleBrowseClick = () => fileInputRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setSelectedFile(file);

    if (file) {
      // Upload the file to the server
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('document', file);

        const response = await ApiService.post('/admin/employee/upload-document', formDataUpload, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (response && response.data && response.data.document_path) {
          setUploadedDocumentPath(response.data.document_path);
          setSnackbar({ open: true, message: 'Document uploaded successfully!', severity: 'success' });
        } else if (response && response.document_path) {
          setUploadedDocumentPath(response.document_path);
          setSnackbar({ open: true, message: 'Document uploaded successfully!', severity: 'success' });
        }
      } catch (error) {
        logger.error('Error uploading document:', error);
        setSnackbar({ open: true, message: 'Failed to upload document. Please try again.', severity: 'error' });
        setSelectedFile(null);
      }
    }
  };

  return (
    <Container>
      <TitleSection>
        <TitleIcon>
          <PersonAddIcon sx={{ fontSize: 26 }} />
        </TitleIcon>
        <Box>
          <Typography variant="h4" fontWeight={700} color="#1f2937">Add New Employee</Typography>
          <Typography variant="body2" color="text.secondary">Create a new team member profile</Typography>
        </Box>
      </TitleSection>

      <FormPaper elevation={0}>
        <form onSubmit={handleSubmit}>
          <SectionTitle>
            <BadgeIcon fontSize="small" /> Employee Information
          </SectionTitle>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <StyledTextField fullWidth label="Full Name" placeholder="e.g. John Doe" name="full_name" value={formData.full_name} onChange={handleChange} required error={!!errors.full_name} helperText={errors.full_name} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField fullWidth label="Employee Code" placeholder="e.g. EMP001" name="employee_code" value={formData.employee_code} onChange={handleChange} required error={!!errors.employee_code} helperText={errors.employee_code} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <StyledFormControl fullWidth error={!!errors.department}>
                <InputLabel id="department-label" shrink>Department</InputLabel>
                <Select labelId="department-label" name="department" value={formData.department} onChange={handleChange} label="Department" displayEmpty renderValue={(s) => s || 'Select'}>
                  {departments.map((dept) => <MenuItem key={dept} value={dept}>{dept}</MenuItem>)}
                </Select>
                {errors.department && <FormHelperText>{errors.department}</FormHelperText>}
              </StyledFormControl>
            </Grid>

            {formData.department === 'Other' && (
              <Grid item xs={12} sm={6}>
                <StyledTextField fullWidth label="Specify Department" placeholder="e.g. IT Support" name="custom_department" value={formData.custom_department} onChange={handleChange} required error={!!errors.department && !formData.custom_department} helperText={!formData.custom_department ? errors.department : ''} />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <StyledFormControl fullWidth error={!!errors.education}>
                <InputLabel id="education-label" shrink>Education</InputLabel>
                <Select labelId="education-label" name="education" value={formData.education} onChange={handleChange} label="Education" displayEmpty renderValue={(s) => s || 'Select'}>
                  {educations.map((e) => <MenuItem key={e} value={e}>{e}</MenuItem>)}
                </Select>
                {errors.education && <FormHelperText>{errors.education}</FormHelperText>}
              </StyledFormControl>
            </Grid>

            <Grid item xs={12}>
              <StyledTextField fullWidth label="Official Email Address" placeholder="john.doe@company.com" name="email" type="email" value={formData.email} onChange={handleChange} required error={!!errors.email} helperText={errors.email} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <StyledTextField fullWidth label="Contact Number" placeholder="+91 ..." name="phone_number" value={formData.phone_number} onChange={handleChange} required error={!!errors.phone_number} helperText={errors.phone_number} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField fullWidth label="Designation" placeholder="e.g. Software Engineer" name="designation" value={formData.designation} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledFormControl fullWidth error={!!errors.role}>
                <InputLabel id="role-label" shrink>Assign Role *</InputLabel>
                <Select
                  labelId="role-label"
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  label="Assign Role *"
                  displayEmpty
                  renderValue={(val) => {
                    const role = roles.find(r => r.id === val);
                    return role ? role.name : 'Select Role';
                  }}
                >
                  {roles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>
                  ))}
                </Select>
                {errors.role && <FormHelperText>{errors.role}</FormHelperText>}
              </StyledFormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledFormControl fullWidth error={!!errors.report_to}>
                <InputLabel id="mgr-label" shrink>Reporting Manager</InputLabel>
                <Select labelId="mgr-label" name="report_to" value={formData.report_to} onChange={handleChange} label="Reporting Manager" displayEmpty renderValue={(val) => {
                  const mgr = managers.find(m => m.id === val);
                  return mgr ? `${mgr.first_name || ''} ${mgr.last_name || ''}`.trim() : 'Select Manager';
                }}>
                  {managers.map((m) => <MenuItem key={m.id} value={m.id}>{m.first_name} {m.last_name}</MenuItem>)}
                </Select>
                {errors.report_to && <FormHelperText>{errors.report_to}</FormHelperText>}
              </StyledFormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField fullWidth label="Alternative Contact" placeholder="Emergency contact" name="alternate_phone_number" value={formData.alternate_phone_number} onChange={handleChange} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <StyledTextField fullWidth label="Date of Joining" name="date_of_joining" type="date" value={formData.date_of_joining} onChange={handleChange} InputLabelProps={{ shrink: true }} required error={!!errors.date_of_joining} helperText={errors.date_of_joining} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledFormControl fullWidth error={!!errors.employee_type}>
                <InputLabel id="type-label" shrink>Employee Type</InputLabel>
                <Select labelId="type-label" name="employee_type" value={formData.employee_type} onChange={handleChange} label="Employee Type" displayEmpty renderValue={(s) => s || 'Select'}>
                  {employeeTypes.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </StyledFormControl>
            </Grid>
          </Grid>



          <SectionTitle>
            <HomeIcon fontSize="small" /> Family & Address
          </SectionTitle>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <StyledTextField fullWidth label="Family Member Name" name="family_member_name" value={formData.family_member_name} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StyledFormControl fullWidth>
                <InputLabel id="relation-label" shrink>Relation</InputLabel>
                <Select labelId="relation-label" name="family_relation" value={formData.family_relation} onChange={handleChange} label="Relation" displayEmpty renderValue={(s) => s || 'Select'}>
                  {relations.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
              </StyledFormControl>
            </Grid>
            {formData.family_relation === 'Other' && (
              <Grid item xs={12} sm={4}>
                <StyledTextField fullWidth label="Specify Relation" name="custom_family_relation" value={formData.custom_family_relation} onChange={handleChange} placeholder="e.g. Spouse" />
              </Grid>
            )}
            <Grid item xs={12} sm={4}>
              <StyledTextField fullWidth label="Family Phone" name="family_contact" value={formData.family_contact} onChange={handleChange} />
            </Grid>
          </Grid>

          <SectionTitle>
            <HomeIcon fontSize="small" /> Residential Address
          </SectionTitle>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <StyledTextField fullWidth label="Address Line 1" name="current_address_line1" value={formData.current_address_line1} onChange={handleChange} required error={!!errors.current_address_line1} helperText={errors.current_address_line1} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StyledTextField fullWidth label="City" name="current_city" value={formData.current_city} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StyledTextField fullWidth label="State" name="current_state" value={formData.current_state} onChange={handleChange} required error={!!errors.current_state} helperText={errors.current_state} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StyledTextField fullWidth label="Postal Code" name="current_zip_code" value={formData.current_zip_code} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StyledTextField fullWidth label="Country" name="current_country" value={formData.current_country} InputProps={{ readOnly: true }} />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', alignItems: 'center', mt: 4, mb: 1 }}>
            <SectionTitle sx={{ m: 0, flex: 1 }}>
              <HomeIcon fontSize="small" /> Permanent Address
            </SectionTitle>
            <Button
              size="small"
              onClick={() => setFormData(prev => ({
                ...prev,
                permanent_address_line1: prev.current_address_line1,
                permanent_city: prev.current_city,
                permanent_state: prev.current_state,
                permanent_zip_code: prev.current_zip_code,
                permanent_country: prev.current_country
              }))}
              sx={{ color: '#3b82f6', fontWeight: 600, textTransform: 'none' }}
            >
              Same as Residential
            </Button>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <StyledTextField fullWidth label="Address Line 1" name="permanent_address_line1" value={formData.permanent_address_line1} onChange={handleChange} required error={!!errors.permanent_address_line1} helperText={errors.permanent_address_line1} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StyledTextField fullWidth label="City" name="permanent_city" value={formData.permanent_city} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StyledTextField fullWidth label="State" name="permanent_state" value={formData.permanent_state} onChange={handleChange} required error={!!errors.permanent_state} helperText={errors.permanent_state} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StyledTextField fullWidth label="Postal Code" name="permanent_zip_code" value={formData.permanent_zip_code} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StyledTextField fullWidth label="Country" name="permanent_country" value={formData.permanent_country} InputProps={{ readOnly: true }} />
            </Grid>
          </Grid>

          <SectionTitle>
            <CloudUploadIcon fontSize="small" /> Attached Documents
          </SectionTitle>
          <Grid container spacing={2}>
            {documents.map((doc, index) => (
              <React.Fragment key={index}>
                <Grid item xs={12} sm={5}>
                  <StyledTextField
                    fullWidth
                    label="Document Title"
                    placeholder="e.g. Resume, ID Proof"
                    value={doc.title}
                    onChange={(e) => handleDocTitleChange(index, e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    sx={{
                      height: '56px',
                      borderRadius: '12px',
                      borderColor: doc.path ? '#10b981' : undefined,
                      color: doc.path ? '#10b981' : undefined,
                      borderStyle: 'dashed'
                    }}
                  >
                    {doc.path ? (doc.file?.name || 'File Uploaded') : 'Upload File'}
                    <input type="file" hidden onChange={(e) => handleDocFileChange(index, e)} />
                  </Button>
                </Grid>
                <Grid item xs={12} sm={2} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {documents.length > 1 && (
                    <IconButton onClick={() => handleRemoveDocument(index)} color="error" size="small" sx={{ bgcolor: '#fee2e2' }}>
                      <DeleteIcon />
                    </IconButton>
                  )}
                  {index === documents.length - 1 && (
                    <IconButton onClick={handleAddDocument} color="primary" size="small" sx={{ bgcolor: '#dbeafe' }}>
                      <AddIcon />
                    </IconButton>
                  )}
                </Grid>
              </React.Fragment>
            ))}
          </Grid>

          {/* Success Dialog */}
          <Dialog open={showSuccessDialog} onClose={handleCloseSuccessDialog} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ color: '#00AA55', display: 'flex', alignItems: 'center', gap: 1 }}>
              <BadgeIcon /> Employee Created Successfully
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1" sx={{ mb: 2 }}>
                The employee <strong>{formData.full_name || 'User'}</strong> has been created with the following credentials:
              </Typography>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: '12px',
                  background: '#fcfcfd',
                  border: '1px solid #eef0f3',
                  mb: 2
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      USERNAME
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                      {generatedCredentials.username}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      PASSWORD
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                        {generatedCredentials.password}
                      </Typography>
                      <IconButton size="small" onClick={() => copyToClipboard(generatedCredentials.password)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              <Button
                variant="contained"
                fullWidth
                startIcon={<PictureAsPdfIcon />}
                onClick={downloadCredentialsPDF}
                sx={{
                  backgroundColor: '#3b82f6',
                  color: '#fff',
                  textTransform: 'none',
                  '&:hover': { backgroundColor: '#2563eb' }
                }}
              >
                Download Credentials PDF
              </Button>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseSuccessDialog} color="primary">
                Close & Go to List
              </Button>
            </DialogActions>
          </Dialog>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 5 }}>
            <Button variant="outlined" sx={{ borderRadius: '12px', px: 4, py: 1.5, borderColor: '#eef0f3', color: '#64748b', textTransform: 'none', fontWeight: 600 }} onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <SubmitButton type="submit" variant="contained" disabled={loading} startIcon={loading && <CircularProgress size={20} color="inherit" />}>
              {loading ? 'Processing...' : 'Create Employee Profile'}
            </SubmitButton>
          </Box>
        </form >
      </FormPaper >

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container >
  );
};

export default AddEmployee;